import moment from 'moment-timezone'

import {BusinessLineType} from '../../common/types'
import {checkEmptyTimeZone} from '../../Organisms/OrderIntake/utils'
import {ShippingType} from '../declarations/OrderIntake.enums'
import {
  OptimizedMaterialDto,
  OptimizedOption,
  OptionsV2,
  OrderIntakeMaterialOptionPayload,
  QuantityType
} from '../declarations/types'

type OptionResult = OrderIntakeMaterialOptionPayload
type OptionResults = OptionResult[]

export const mapOptimizedOptionsToLegacy = (
  o: OptimizedOption[],
  applyMaterialEnteredDescription: boolean,
  applyContractItemDescription: boolean,
  customerId?: string
): OptionResults => {
  const result = o.reduce((siteResult, site) => {
    const optionsToSiteResult = site.contractUnit.reduce((contractUnitResult, contract) => {
      // eslint-disable-next-line complexity
      const optionsToContractUnit = contract.materials.reduce((materialResult, material) => {
        const option: OptionResult = {
          businessHours: {
            ...site.businessHours[material.shippingType.toLowerCase()],
            timeZone: checkEmptyTimeZone(
              material.shippingType === ShippingType.DELIVER
                ? site.businessHours[material.shippingType.toLowerCase()].timeZone
                : contract.timeZone
            )
          },
          canDisableEmailSending: !!material.canDisableEmailSending,
          contact: contract.contact,
          customerId,
          customerNumber: contract.customerNumber ?? '',
          customerReference: material.poNumber ?? '',
          dateRange: {
            ...site.dateRange[material.shippingType.toLowerCase()],
            from: contract.contractValidityPeriods[material.shippingType.toLowerCase()]
              .contractValidityStartDate,
            to: contract.contractValidityPeriods[material.shippingType.toLowerCase()]
              .contractValidityEndDate
          },
          shippingType: material.shippingType,
          deactivationReason: material.deactivationReason,
          defaultDeliveryWindow: site.defaultDeliveryWindow[material.shippingType.toLowerCase()],
          dispatchPhoneNumbers: site.dispatchPhoneNumbers,
          driverInstructions: contract.driverInstructions ?? '',
          invalid: !!material.invalid,
          material: {
            businessLine: material.businessLine ?? BusinessLineType.CEM,
            contractItemPositionNumber: material.contractItemPositionNumber ?? '',
            contractNumber: material.contractNumber ?? '',
            materialDescription: resolveMaterialDescription(
              material,
              applyMaterialEnteredDescription,
              applyContractItemDescription
            ),
            materialNumber: material.materialNumber ?? '',
            materialEnteredNumber: material.materialEnteredNumber ?? '',
            quantityType: material.quantityType ?? QuantityType.TruckCapacity,
            isCarbonSavingProduct: material.isCarbonSavingProduct ?? false,
            bomMaterials: material.bomMaterials ?? [],
            packagingType: material.packagingType ?? 'L',
            orgUnitId: material.orgUnitId ?? ''
          },
          plant: {
            city: site.plant.city ?? '',
            country: site.plant.country ?? '',
            plantName: site.plant.plantName ?? '',
            plantNumber: site.plant.plantNumber ?? '',
            postalCode: site.plant.postalCode ?? '',
            state: site.plant.state ?? '',
            street: site.plant.street ?? '',
            configurableSlots: site.plant.configurableSlots ?? []
          },
          shippingAddress: {
            city: contract.city ?? '',
            country: contract.country ?? '',
            postalCode: contract.postalCode ?? '',
            state: contract.state ?? '',
            street: contract.street ?? '',
            siteNumber: contract.siteNumber,
            siteName: contract.siteName ?? ''
          },
          truckCapacity: (contract.truckCapacity ?? {})[material.shippingType.toLowerCase()] ?? {}
        }
        materialResult.push(option)
        return materialResult
      }, [] as OptionResults)

      return contractUnitResult.concat(optionsToContractUnit)
    }, [] as OptionResults)

    return siteResult.concat(optionsToSiteResult)
  }, [] as OptionResults)
  return result
}

export const mapV2OptionsToLegacy = (
  options: OptionsV2,
  applyMaterialEnteredDescription: boolean,
  applyContractItemDescription: boolean
): OptionResults => {
  const result: OptionResult[] = []
  options.contracts?.forEach((contract) => {
    // eslint-disable-next-line complexity
    contract.materials?.forEach((material) => {
      const site = options.sites?.find((s) => s.contractNumbers?.includes(contract.contractNumber))
      const plant = options.plants?.find((p) => p.plantNumber === material.plantNumber)
      const configuration = options.configurations?.find(
        (c) => c.shippingType.toLowerCase() === material.shippingType.toLowerCase()
      )

      if (site && plant && configuration) {
        const payload: OptionResult = {
          businessHours: {
            cutOffTimeThreshold: configuration.allowedBusinessHours?.cutoffTimeThreshold ?? '',
            earliestPossible: configuration.allowedBusinessHours?.earliestPossibleLoadTime ?? '',
            latestPossible: configuration.allowedBusinessHours?.latestPossibleLoadTime ?? '',
            minInterval: configuration.allowedBusinessHours?.minInterval ?? '',
            minFixIntervalTime: configuration.allowedBusinessHours?.minFixTimeOrderInterval ?? '',
            timeChangeInterval: configuration.allowedBusinessHours?.timeChangeInterval ?? '',
            timeZone: checkEmptyTimeZone(configuration.allowedBusinessHours?.timeZone)
          },
          canDisableEmailSending: material.canDisableEmailSending ?? false,
          contact: site.contacts
            ? site.contacts[0]
            : {
                mainPhone: '',
                fullName: '',
                backupPhone: '',
                siteContactName: '',
                carbonCopyEmail: ''
              },
          customerNumber: contract.customerNumber,
          customerId: contract.customerId ?? '',
          customerReference: material.poNumber ?? '',
          dateRange: {
            from: contract.contractValidityPeriods
              ? contract.contractValidityPeriods[material.shippingType.toLowerCase()]
                  .contractValidityStartDate
              : '',
            to: contract.contractValidityPeriods
              ? contract.contractValidityPeriods[material.shippingType.toLowerCase()]
                  .contractValidityEndDate
              : '',
            fromAfterCutoff: configuration.validOrderRangeAfterCutoffTimeFrom
              ? configuration.validOrderRangeAfterCutoffTimeFrom
              : '',
            toAfterCutoff: configuration.validOrderRangeAfterCutoffTimeTo
              ? configuration.validOrderRangeAfterCutoffTimeTo
              : '',
            exceptions: {
              weekends: configuration.dateExceptions?.weekends
                ? configuration.dateExceptions.weekends
                : [],
              publicHolidays: configuration.dateExceptions?.publicHolidays
                ? configuration.dateExceptions.publicHolidays
                : [],
              excludedDates: configuration.dateExceptions?.excludedDays
                ? configuration.dateExceptions.excludedDays
                : []
            },
            businessDays: configuration.allowedBusinessDays?.map((day) => {
              return {
                dayOfWeek: day.dayOfWeek,
                leadTime: day.leadTime,
                cutOffTime: {
                  dates: [],
                  timestamp: toTodaysCutOffTimestamp(
                    day.cutOffTime,
                    checkEmptyTimeZone(configuration.allowedBusinessHours?.timeZone)
                  )
                }
              }
            })
          },
          shippingType: material.shippingType,
          deactivationReason: material.deactivationReason ?? '',
          defaultDeliveryWindow: {
            defaultLatestLoadTime: configuration?.allowedBusinessHours?.defaultLatestLoadTime ?? '',
            defaultEarliestLoadTime:
              configuration?.allowedBusinessHours?.defaultEarliestLoadTime ?? ''
          },
          dispatchPhoneNumbers: site.contacts
            ? site.contacts.map((phone) => phone.dispatchPhone!)
            : [],
          driverInstructions: contract.driverInstructions ?? '',
          invalid: material.invalid ?? false,
          material: {
            businessLine: material.businessLine ?? BusinessLineType.CEM,
            contractItemPositionNumber: material.contractItemPositionNumber ?? '',
            contractNumber: contract.contractNumber ?? '',
            orgUnitId:
              contract.orgUnitId ?? material.contractItemPositionNumber ?? material.poNumber ?? '',
            materialDescription: resolveMaterialDescription(
              material,
              applyMaterialEnteredDescription,
              applyContractItemDescription
            ),
            materialNumber: material.materialNumber ?? '',
            materialEnteredNumber: material.materialEnteredNumber ?? material.materialNumber ?? '',
            quantityType: material.quantityType ?? QuantityType.TruckCapacity,
            isCarbonSavingProduct: material.isCarbonSavingProduct ?? false,
            packagingType: material.packagingType ?? 'L',
            materialId: material.materialId,
            bomMaterials: material.bomMaterials ?? []
          },
          plant: {
            city: plant.city ?? '',
            country: plant.country ?? '',
            plantName: plant.plantName ?? '',
            plantNumber: plant.plantNumber ?? '',
            postalCode: plant.postalCode ?? '',
            state: plant.state ?? '',
            street: plant.street ?? '',
            configurableSlots: plant.configurableSlots ?? [],
            plantId: plant.plantId
          },
          shippingAddress: {
            city: site.city ?? '',
            country: site.country ?? '',
            postalCode: site.postalCode ?? '',
            state: site.state ?? '',
            street: site.street ?? '',
            siteNumber: site.siteNumber,
            siteId: site.siteId ?? site.siteNumber ?? '',
            siteName: site.siteName ?? ''
          },
          truckCapacity: {
            capacity: configuration?.allowedTruckCapacity?.default ?? 0,
            capacityUom: configuration?.allowedTruckCapacity?.uom ?? '',
            maxCapacity: configuration?.allowedTruckCapacity?.max ?? 0,
            minCapacity: configuration?.allowedTruckCapacity?.min ?? 0,
            numberOfDecimals: configuration?.allowedTruckCapacity?.allowedDecimals ?? 0,
            shortLoadThreshold: 0
          }
        }

        result.push(payload)
      }
    })
  })

  return result
}

// V2 sends only the UTC time of today's cutoff (HH:mm:ss); "today" is the plant-local day.
export const toTodaysCutOffTimestamp = (utcTime: string | undefined, timeZone: string): string => {
  if (!utcTime) return ''

  const localToday = moment().tz(timeZone).format('YYYY-MM-DD')
  const cutOff = [-1, 0, 1]
    .map((dayOffset) =>
      moment.utc(`${localToday} ${utcTime}`, 'YYYY-MM-DD HH:mm:ss', true).add(dayOffset, 'days')
    )
    .find(
      (candidate) =>
        candidate.isValid() && candidate.clone().tz(timeZone).format('YYYY-MM-DD') === localToday
    )

  return cutOff ? cutOff.format('YYYY-MM-DD[T]HH:mm:ss[Z]') : ''
}

const resolveMaterialDescription = (
  material: OptimizedMaterialDto,
  applyMaterialEnteredDescription: boolean,
  applyContractItemDescription: boolean
): string => {
  if (applyMaterialEnteredDescription)
    return material.materialEnteredDescription
      ? material.materialEnteredDescription
      : (material.materialDescription ?? '')

  if (applyContractItemDescription)
    return material.contractItemDescription
      ? material.contractItemDescription
      : (material.materialDescription ?? '')

  return material.materialDescription ?? ''
}
